<template>
  <div class="affiliate-activity-card affiliate-links-card" :class="{ 'is-preview': preview }">
    <div class="affiliate-activity-card__head">
      <div class="affiliate-activity-card__title-row">
        <div class="affiliate-activity-card__title">
          {{ preview ? "Your referral links & codes" : "Referral links & codes" }}
        </div>
        <button
          v-if="preview"
          type="button"
          class="affiliate-activity-view-all"
          @click="$emit('view-all')"
        >
          View all
        </button>
      </div>
      <p v-if="preview" class="affiliate-activity-card__sub">Latest {{ displayedRows.length }}.</p>
    </div>

    <div v-if="!preview" class="affiliate-activity-filters">
      <input
        v-model="search"
        type="text"
        class="affiliate-activity-search"
        placeholder="Search label or code"
        @input="page = 1"
      />
      <select v-model="typeFilter" class="affiliate-activity-select" @change="page = 1">
        <option value="all">All types</option>
        <option value="link">Link</option>
        <option value="code">Code</option>
      </select>
      <select v-model="sortBy" class="affiliate-activity-select">
        <option value="created">Sort: Created</option>
        <option value="earned">Sort: Earned</option>
        <option value="label">Sort: Label</option>
        <option value="signups">Sort: Signups</option>
      </select>
    </div>

    <div class="table-responsive">
      <table class="affiliate-activity-table affiliate-links-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Link / Code</th>
            <th>{{ preview ? "Your share" : "You earn" }}</th>
            <th>{{ preview ? "Their discount" : "Discount" }}</th>
            <th>Signups</th>
            <th>{{ preview ? "Orders" : "Orders / Uses" }}</th>
            <th>Earned</th>
            <th v-if="!preview">Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!displayedRows.length">
            <td :colspan="preview ? 8 : 9" class="affiliate-activity-empty">No referral links yet.</td>
          </tr>
          <tr v-for="row in displayedRows" :key="row.id || row.referral_url">
            <td class="affiliate-links-label">
              <div class="affiliate-activity-user">{{ row.affiliate || "—" }}</div>
            </td>
            <td>
              <div class="affiliate-link-cell">
                <span class="affiliate-link-icon" :class="isCode(row) ? 'is-code' : 'is-link'">
                  <svg v-if="isCode(row)" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M7 8h10M7 12h6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                  <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M10 14a5 5 0 007.07 0l1.41-1.41a5 5 0 00-7.07-7.07L10 6.93" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <path d="M14 10a5 5 0 00-7.07 0L5.52 11.41a5 5 0 007.07 7.07L14 17.07" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </span>
                <span
                  class="affiliate-link-text"
                  :class="{ 'is-code': isCode(row) }"
                  v-b-tooltip.hover.top="linkHoverText(row)"
                >{{ linkDisplay(row) }}</span>
              </div>
            </td>
            <td class="affiliate-earn-cell">{{ formatPercent(row.commision) }}</td>
            <td class="affiliate-discount-cell">{{ formatPercent(row.discount) }}</td>
            <td>{{ displayCount(rowStats(row).signups) }}</td>
            <td>{{ displayCount(rowStats(row).orders) }}</td>
            <td :class="preview ? 'affiliate-earn-cell' : ''">{{ money(rowStats(row).earned) }}</td>
            <td v-if="!preview">{{ formatDate(createdValue(row)) }}</td>
            <td class="affiliate-links-actions">
              <div class="affiliate-links-actions__row">
                <button type="button" class="affiliate-icon-btn" title="Copy" @click="copyText(row)">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect x="8" y="8" width="11" height="13" rx="2" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2h2" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                </button>
                <button
                  type="button"
                  class="affiliate-icon-btn"
                  title="Edit"
                  @click="openEdit(row)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 20h4l10.5-10.5a2.12 2.12 0 00-3-3L5 17v3z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-if="!preview && filteredRows.length">
      <div class="affiliate-activity-footer">
        <div class="total-records-list">
          <p>Showing {{ displayedRows.length }} of {{ filteredRows.length }} records</p>
        </div>
        <div class="per-page-right-box">
          <div class="select-item">
            <select v-model.number="perPage" @change="page = 1">
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
              <option :value="500">500</option>
            </select>
          </div>
        </div>
      </div>
      <div class="pagination-list" v-if="lastPage > 1">
        <paginate
          :click-handler="goToPage"
          :prev-text="'Prev'"
          :next-text="'Next'"
          :page-count="lastPage"
          :page-range="3"
          :margin-pages="2"
          :container-class="'className'"
          :records="filteredRows.length"
          v-model="page"
          :per-page="perPage"
        />
      </div>
    </template>

    <b-modal
      :id="preview ? 'affiliate-url-edit-preview' : 'affiliate-url-edit'"
      v-model="editModalOpen"
      class="affiliate-modal-box"
      centered
      title="Update Affiliate URL"
      :no-close-on-backdrop="true"
      hide-footer
    >
      <form>
        <b-row>
          <b-col md="12">
            <div class="form-group">
              <label>Link label <span class="affiliate-referral-pick">(only you see this)</span></label>
              <b-form-input v-model="label" placeholder="Spring partners" />
            </div>
          </b-col>
          <b-col md="12">
            <div class="form-group">
              <label>Update Commission Rate</label>
              <b-form-input
                v-model="commission"
                :class="{ 'is-invalid': !!commissionError }"
                @input="setAffiliateUrl"
                @keypress="validateInput"
                placeholder="5"
              />
              <span v-if="commissionError" class="required-text invalid-feedback" style="display: block" role="alert">
                <strong>{{ commissionError }}</strong>
              </span>
            </div>
          </b-col>
          <b-col md="12">
            <div class="form-group">
              <label>Update Discount Rate</label>
              <b-form-input :value="discount" readonly :class="{ 'is-invalid': !!discountError }" placeholder="5" />
              <span v-if="!discountError" class="affiliate-referral-autofill">
                Auto-filled: 10% minus your share.
              </span>
              <span v-if="discountError" class="required-text invalid-feedback" style="display: block" role="alert">
                <strong>{{ discountError }}</strong>
              </span>
            </div>
          </b-col>
          <b-col md="12" v-if="!AffiliateUrlHide">
            <div class="form-group">
              <label>Referral URL</label>
              <b-form-input v-model="referralLink" readonly />
            </div>
          </b-col>
          <b-col md="12">
            <div class="btn-box-list">
              <button type="button" class="affiliate-referral-continue" @click="updateAffiliateUrl">
                Update URL
              </button>
            </div>
          </b-col>
        </b-row>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { numberFormat } from "@/prod.js";
import { truncateMoney } from "@/utils/money.js";
import { useToast } from "vue-toastification";
import Paginate from "vuejs-paginate-next";
import moment from "moment";
import url from "@/prod.js";
import { referralSlugFromValue, shortReferralUrl } from "@/utils/affiliateRef";
const toast = useToast();

export default {
  props: {
    url_lists: {
      type: Array,
      default: () => [],
    },
    earning_lists: {
      type: Array,
      default: () => [],
    },
    preview: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["view-all", "updated"],
  components: {
    Paginate,
  },
  watch: {
    url_lists: {
      immediate: true,
      handler(list) {
        this.urllists = Array.isArray(list) ? list : [];
        this.page = 1;
      },
    },
    userData(newVal) {
      this.userData.emailVerified = !!newVal.emailVerified;
    },
  },
  mounted() {
    if (this.userData.role != "Agency" || !this.userData.emailVerified) {
      this.$router.push("/dashboard");
    }
  },
  data() {
    return {
      numberFormat,
      truncateMoney,
      loading: false,
      urllists: [],
      appURL: url.appUrl,
      referralLink: "",
      label: "",
      commission: "",
      discount: "",
      errors: [],
      encodedUrl: "",
      AffiliateUrlHide: true,
      updateId: "",
      page: 1,
      perPage: 50,
      search: "",
      typeFilter: "all",
      sortBy: "created",
      editModalOpen: false,
    };
  },
  methods: {
    goToPage(pageNum) {
      this.page = pageNum;
    },
    money(value) {
      return this.numberFormat(this.truncateMoney(value), "currency");
    },
    formatPercent(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return "0%";
      return `${number % 1 === 0 ? number : number.toFixed(2)}%`;
    },
    formatDate(value) {
      if (!value) return "—";
      if (typeof value === "number" || /^\d{10,13}$/.test(String(value))) {
        const ms = String(value).length > 10 ? Number(value) : Number(value) * 1000;
        const fromUnix = moment(ms);
        if (fromUnix.isValid()) return fromUnix.format("MMM DD");
      }
      const formats = [
        moment.ISO_8601,
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD/MM/YYYY",
        "MMM DD, YYYY",
      ];
      const strict = moment(value, formats, true);
      if (strict.isValid()) return strict.format("MMM DD");
      const loose = moment(value);
      return loose.isValid() ? loose.format("MMM DD") : "—";
    },
    createdValue(row) {
      return (
        row.created_at ||
        row.createdAt ||
        row.created ||
        row.date_created ||
        row.date ||
        row.added_at ||
        row.updated_at ||
        ""
      );
    },
    firstNumber(...values) {
      for (const value of values) {
        if (value !== undefined && value !== null && value !== "") {
          return Number(value);
        }
      }
      return null;
    },
    earningsForRow(row) {
      const label = String(row.affiliate || "").trim().toLowerCase();
      const refUrl = String(row.referral_url || "").trim().toLowerCase();
      const queryMatch = refUrl.match(/[?&]ref=([^&]+)/i);
      const refParam = queryMatch ? decodeURIComponent(queryMatch[1]).toLowerCase() : "";
      return this.earning_lists.filter((item) => {
        const via = String(item.via || item.affiliate || item.slug || item.source_name || "").toLowerCase();
        const itemUrl = String(item.referral_url || item.source_url || "").toLowerCase();
        if (refUrl && itemUrl && (itemUrl === refUrl || itemUrl.includes(refUrl))) return true;
        if (refParam && (via === refParam || itemUrl.includes(refParam))) return true;
        if (label && via && via === label) return true;
        return false;
      });
    },
    displayCount(value) {
      if (value === undefined || value === null || value === "") return "—";
      return value;
    },
    isCode(row) {
      const type = String(row.type || row.link_type || "").toLowerCase();
      if (type.includes("code")) return true;
      if (type.includes("link")) return false;
      const ref = String(row.referral_url || "");
      return !!row.code || (!/^https?:\/\//i.test(ref) && !ref.includes("ref="));
    },
    linkHoverText(row) {
      if (this.isCode(row)) {
        return String(row.code || row.referral_url || this.linkDisplay(row) || "");
      }
      return this.fullReferralLink(row) || this.linkDisplay(row);
    },
    linkDisplay(row) {
      if (this.isCode(row)) {
        return String(row.code || row.referral_url || "").toUpperCase();
      }
      const full = this.fullReferralLink(row);
      return full.replace(/^https?:\/\//i, "") || String(row.referral_url || "");
    },
    fullReferralLink(row) {
      const slug = referralSlugFromValue(row.referral_url || row.code);
      return shortReferralUrl(slug, this.appURL);
    },
    rowStats(row) {
      const matches = this.earningsForRow(row);
      return {
        signups: this.firstNumber(row.signups, row.total_signups, row.signup_count),
        orders: this.firstNumber(row.orders, row.total_orders, row.order_count, matches.length),
        earned: this.firstNumber(
          row.earned,
          row.total_earning,
          matches.reduce((sum, item) => sum + (Number(item.earned_bonus) || 0), 0)
        ),
      };
    },
    openEdit(row) {
      this.currentAffiliateUrl(row.id);
      this.editModalOpen = true;
    },
    currentAffiliateUrl(id) {
      const result = this.urllists.find((item) => item.id === id);
      if (!result) return;
      this.updateId = id;
      this.label = result.affiliate && result.affiliate !== "—" ? result.affiliate : "";
      this.commission = parseFloat(result.commision).toFixed(2);
      this.discount = parseFloat(result.discount).toFixed(2);
      this.referralLink = result.referral_url;
      const match = String(result.referral_url || "").match(/[?&]ref=([^&]+)/i);
      this.encodedUrl = result.code || (match ? decodeURIComponent(match[1]) : result.referral_url);
    },
    setAffiliateUrl() {
      const raw = String(this.commission ?? "").trim();
      if (raw === "" || raw === "-") {
        this.discount = "";
        return;
      }
      const share = Number(raw);
      if (!Number.isFinite(share)) {
        this.discount = "";
        return;
      }
      const discount = 10 - share;
      this.discount = Number.isInteger(discount) ? discount : Number(discount.toFixed(2));
    },
    async updateAffiliateUrl() {
      this.errors = [];
      if (this.commissionError || this.discountError) return;
      this.loading = true;
      try {
        let res = await this.$store.dispatch("AgencyUsers/updateAffiliateUrlDataAction", {
          id: this.updateId,
          commission: this.commission,
          discount: this.discount,
          referral_url: this.encodedUrl,
          label: this.label,
        });
        if (res.success) {
          const row = this.urllists.find((item) => item.id === this.updateId);
          const message =
            res.message ||
            (row && this.isCode(row) ? "Referral code updated" : "Referral link updated");
          if (row) {
            const index = this.urllists.findIndex((item) => item.id === this.updateId);
            this.urllists.splice(index, 1, {
              ...row,
              affiliate: this.label,
              commision: this.commission,
              discount: this.discount,
            });
          }
          this.editModalOpen = false;
          toast.success(message, { timeout: 3000 });
          this.$emit("updated", message);
        } else {
          toast.error(res.error, { timeout: 3000 });
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.errors = error.response?.data?.errors || [];
      }
    },
    copyText(row) {
      const isCode = this.isCode(row);
      const value = isCode
        ? String(row.code || row.referral_url || "")
        : this.fullReferralLink(row) || String(row.referral_url || "");
      const tempInput = document.createElement("input");
      tempInput.value = value;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      toast.success(isCode ? "Code copied" : "URL copied", { timeout: 1500 });
    },
    validateInput(event) {
      const char = event.key;
      const input = event.target.value;
      if (
        !/^\d$/.test(char) &&
        char !== "." &&
        char !== "Backspace" &&
        char !== "ArrowLeft" &&
        char !== "ArrowRight" &&
        char !== "Delete"
      ) {
        event.preventDefault();
      }
      if (char === "." && (input.includes(".") || input === "")) {
        event.preventDefault();
      }
    },
  },
  computed: {
    commissionError() {
      if (this.errors.commission && this.errors.commission[0]) return this.errors.commission[0];
      const raw = String(this.commission ?? "").trim();
      if (raw === "") return "";
      const share = Number(raw);
      if (!Number.isFinite(share)) return "The commission must be a number.";
      if (share > 10) return "The commission may not be greater than 10.";
      if (share < 0) return "The commission must be at least 0.";
      return "";
    },
    discountError() {
      if (this.errors.discount && this.errors.discount[0]) return this.errors.discount[0];
      if (this.discount === "" || this.discount === null) return "";
      const discount = Number(this.discount);
      if (!Number.isFinite(discount)) return "The discount must be a number.";
      if (discount < 0) return "The discount must be at least 0.";
      if (discount > 10) return "The discount may not be greater than 10.";
      return "";
    },
    userData() {
      const data = this.$store.getters["Auth/getUser"];
      return {
        name: data.name,
        email: data.email,
        balance: data.balance,
        role: data.role,
        profileImage: data.image,
        referral_id: data.referral_id,
        emailVerified: data.is_email_verified,
      };
    },
    sortedRows() {
      const rows = [...this.urllists];
      return rows.sort((a, b) => {
        const aStats = this.rowStats(a);
        const bStats = this.rowStats(b);
        if (this.sortBy === "label") {
          return String(a.affiliate || "").localeCompare(String(b.affiliate || ""));
        }
        if (this.sortBy === "signups") {
          return (Number(bStats.signups) || 0) - (Number(aStats.signups) || 0);
        }
        if (this.sortBy === "created") {
          const aId = Number(a.id) || 0;
          const bId = Number(b.id) || 0;
          if (bId !== aId) return bId - aId;
          const aDate = moment(this.createdValue(a), ["MM/DD/YYYY", moment.ISO_8601], true);
          const bDate = moment(this.createdValue(b), ["MM/DD/YYYY", moment.ISO_8601], true);
          if (aDate.isValid() && bDate.isValid()) return bDate.valueOf() - aDate.valueOf();
          return 0;
        }
        return (Number(bStats.earned) || 0) - (Number(aStats.earned) || 0);
      });
    },
    filteredRows() {
      if (this.preview) return this.sortedRows;
      return this.sortedRows.filter((row) => {
        const query = this.search.trim().toLowerCase();
        const haystack = [row.affiliate, row.referral_url, row.code, this.linkDisplay(row)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (query && !haystack.includes(query)) return false;
        if (this.typeFilter === "link" && this.isCode(row)) return false;
        if (this.typeFilter === "code" && !this.isCode(row)) return false;
        return true;
      });
    },
    lastPage() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.perPage));
    },
    displayedRows() {
      if (this.preview) return this.sortedRows.slice(0, 5);
      const start = (this.page - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
  },
};
</script>
